import { confirmDialog } from '../shared/rc-confirm-dialog';
import { placeDescriptionsApi, type PlaceDescriptionDetailDto, type PlaceDescriptionSummaryDto, type PlaceDescriptionTemplateDto, type RentalAgreementDto, type TenantDto } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { t as translate, translatePlaceDescriptionCondition, translatePlaceDescriptionKind } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { escapeHtml, formatDate, TENANT_ICONS } from './tenant-shared';

type PropertyOption = {
  id: string;
  address: string;
};

function appendModalOverlay(content: string, zIndex?: string): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  if (zIndex) {
    overlay.style.zIndex = zIndex;
  }
  overlay.innerHTML = content;
  document.body.appendChild(overlay);
  return overlay;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function getPropertyOptions(agreements: RentalAgreementDto[]): PropertyOption[] {
  const unique = new Map<string, PropertyOption>();
  agreements.forEach((agreement) => {
    if (!unique.has(agreement.propertyId)) {
      unique.set(agreement.propertyId, { id: agreement.propertyId, address: agreement.propertyAddress });
    }
  });

  return [...unique.values()].sort((left, right) => left.address.localeCompare(right.address));
}

function renderSummaryCard(summary: PlaceDescriptionSummaryDto): string {
  return `
    <div class="place-description-card">
      <div class="place-description-card-header">
        <div>
          <div class="place-description-card-title">${escapeHtml(summary.title)}</div>
          <div class="place-description-card-meta">
            <span class="badge badge-info">${escapeHtml(translatePlaceDescriptionKind(summary.kind))}</span>
            <span>${escapeHtml(summary.propertyAddress)}</span>
            <span>${escapeHtml(formatDate(summary.inspectionDate))}</span>
          </div>
        </div>
        <div class="place-description-stats">
          <span>${summary.sectionCount} ${translate('placeDescription.stats.sections')}</span>
          <span>${summary.itemCount} ${translate('placeDescription.stats.items')}</span>
          <span>${summary.photoCount} ${translate('placeDescription.stats.photos')}</span>
        </div>
      </div>
      ${summary.generalNotes ? `<p class="place-description-card-notes">${escapeHtml(summary.generalNotes)}</p>` : ''}
      <div class="card-actions">
        <button class="btn btn-outline btn-sm" data-action="edit" data-id="${summary.id}">${translate('common.edit')}</button>
        <button class="btn btn-outline btn-sm" data-action="export" data-id="${summary.id}">${translate('common.export')}</button>
        <button class="btn btn-danger btn-sm" data-action="delete" data-id="${summary.id}">${translate('common.delete')}</button>
      </div>
    </div>
  `;
}

export async function openPlaceDescriptionsDrawer(tenant: TenantDto, agreements: RentalAgreementDto[]): Promise<void> {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'modal modal-wide';

  let summaries: PlaceDescriptionSummaryDto[] = [];
  let templates: PlaceDescriptionTemplateDto[] = [];
  let loading = true;
  let error = '';
  let actionError = '';

  const propertyOptions = getPropertyOptions(agreements);

  const close = () => overlay.remove();

  const load = async () => {
    loading = true;
    error = '';
    actionError = '';
    render();

    try {
      [summaries, templates] = await Promise.all([
        placeDescriptionsApi.getByTenant(tenant.id),
        placeDescriptionsApi.getTemplates(),
      ]);
    } catch (err) {
      console.error('Failed to load place descriptions:', err);
      error = err instanceof Error ? err.message : translate('placeDescription.loadError');
    } finally {
      loading = false;
      render();
    }
  };

  const render = () => {
    modal.innerHTML = `
      <div class="modal-header">
        <div>
          <h2>${translate('placeDescription.title')}</h2>
          <p class="modal-subtitle">${escapeHtml(`${tenant.firstName} ${tenant.lastName}`)}</p>
        </div>
        <button class="modal-close" data-action="close">${TENANT_ICONS.close}</button>
      </div>
      <div class="tab-toolbar place-description-toolbar">
        <p class="place-description-toolbar-copy">${translate('placeDescription.drawerHelp')}</p>
        <button class="btn btn-primary btn-sm" data-action="add" ${propertyOptions.length === 0 || templates.length === 0 ? 'disabled' : ''}>
          ${TENANT_ICONS.plusSm}
          ${translate('placeDescription.add')}
        </button>
      </div>
      ${actionError ? `<div class="form-error">${escapeHtml(actionError)}</div>` : ''}
      ${loading
        ? `<div class="empty-state"><p>${translate('common.loading')}</p></div>`
        : error
          ? `<div class="empty-state"><p>${escapeHtml(error)}</p></div>`
          : summaries.length === 0
            ? `<div class="empty-state"><p>${translate('placeDescription.empty')}</p></div>`
            : `<div class="place-description-list">${summaries.map(renderSummaryCard).join('')}</div>`}
    `;

    modal.querySelector('[data-action="close"]')?.addEventListener('click', close);
    modal.querySelector('[data-action="add"]')?.addEventListener('click', async () => {
      const created = await openCreatePlaceDescriptionModal(tenant, propertyOptions, templates);
      if (!created) {
        return;
      }

      await load();
      await openPlaceDescriptionEditorModal(created.id);
      await load();
    });

    modal.querySelectorAll<HTMLElement>('[data-action="edit"]').forEach((button) => button.addEventListener('click', async () => {
      await openPlaceDescriptionEditorModal(button.dataset.id!);
      await load();
    }));

    modal.querySelectorAll<HTMLElement>('[data-action="export"]').forEach((button) => button.addEventListener('click', async () => {
      try {
        actionError = '';
        const file = await placeDescriptionsApi.export(button.dataset.id!);
        downloadBlob(file.blob, file.fileName);
      } catch (err) {
        console.error('Failed to export place description:', err);
        actionError = err instanceof Error ? err.message : translate('placeDescription.exportError');
        render();
      }
    }));

    modal.querySelectorAll<HTMLElement>('[data-action="delete"]').forEach((button) => button.addEventListener('click', async () => {
      if (!await confirmDialog(translate('placeDescription.confirmDelete'))) {
        return;
      }

      try {
        actionError = '';
        await placeDescriptionsApi.delete(button.dataset.id!);
        await load();
      } catch (err) {
        console.error('Failed to delete place description:', err);
        actionError = err instanceof Error ? err.message : translate('common.errorGeneric');
        render();
      }
    }));
  };

  overlay.appendChild(modal);
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  document.body.appendChild(overlay);
  await load();
}

async function openCreatePlaceDescriptionModal(
  tenant: TenantDto,
  propertyOptions: PropertyOption[],
  templates: PlaceDescriptionTemplateDto[],
): Promise<PlaceDescriptionDetailDto | null> {
  if (propertyOptions.length === 0 || templates.length === 0) {
    toastService.error(translate('placeDescription.createUnavailable'));
    return null;
  }

  const overlay = appendModalOverlay(`
    <div class="modal">
      <div class="modal-header">
        <h2>${translate('placeDescription.add')}</h2>
        <button class="modal-close" data-action="cancel">${TENANT_ICONS.close}</button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group">
          <label>${translate('common.property')} *</label>
          <select name="propertyId" required>
            ${propertyOptions.map((option) => `<option value="${option.id}">${escapeHtml(option.address)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>${translate('placeDescription.templateLabel')} *</label>
          <select name="templateId" required>
            ${templates.map((template) => `<option value="${template.id}">${escapeHtml(template.name)}</option>`).join('')}
          </select>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>${translate('common.type')} *</label>
            <select name="kind" required>
              <option value="MoveIn">${translate('placeDescription.kind.moveIn')}</option>
              <option value="MoveOut">${translate('placeDescription.kind.moveOut')}</option>
            </select>
          </div>
          <div class="form-group">
            <label>${translate('common.date')} *</label>
            <input name="inspectionDate" type="date" required value="${new Date().toISOString().slice(0, 10)}">
          </div>
        </div>
        <div class="form-group">
          <label>${translate('common.title')}</label>
          <input name="title" placeholder="${translate('placeDescription.titlePlaceholder')}">
        </div>
        <div class="form-group">
          <label>${translate('common.notes')}</label>
          <textarea name="generalNotes" rows="4" placeholder="${translate('placeDescription.notesPlaceholder')}"></textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${translate('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${translate('common.create')}</button>
        </div>
      </form>
    </div>
  `, '210');

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  return await new Promise<PlaceDescriptionDetailDto | null>((resolve) => {
    overlay.querySelector('form')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submitButton = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
      const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }
      submitButton.disabled = true;
      submitButton.textContent = translate('common.saving');

      const formData = new FormData(event.target as HTMLFormElement);

      try {
        const created = await placeDescriptionsApi.create(tenant.id, {
          propertyId: formData.get('propertyId') as string,
          templateId: formData.get('templateId') as string,
          kind: formData.get('kind') as string,
          inspectionDate: formData.get('inspectionDate') as string,
          title: ((formData.get('title') as string) || '').trim() || undefined,
          generalNotes: ((formData.get('generalNotes') as string) || '').trim() || undefined,
        });

        close();
        resolve(created);
      } catch (err) {
        console.error('Failed to create place description:', err);
        submitButton.disabled = false;
        submitButton.textContent = translate('common.create');
        if (errorBox) {
          errorBox.hidden = false;
          errorBox.textContent = err instanceof Error ? err.message : translate('common.errorGeneric');
        }
      }
    });
  });
}

async function openPlaceDescriptionEditorModal(placeDescriptionId: string): Promise<void> {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.style.zIndex = '220';

  const modal = document.createElement('div');
  modal.className = 'modal modal-wide place-description-editor-modal';

  let detail: PlaceDescriptionDetailDto | null = null;
  let loading = true;
  let error = '';
  let formError = '';
  let saving = false;
  let busyItemId = '';

  const close = () => overlay.remove();

  const load = async () => {
    loading = true;
    error = '';
    render();

    try {
      detail = await placeDescriptionsApi.getById(placeDescriptionId);
    } catch (err) {
      console.error('Failed to load place description:', err);
      error = err instanceof Error ? err.message : translate('placeDescription.loadError');
    } finally {
      loading = false;
      render();
    }
  };

  const buildUpdatePayload = (): any => {
    if (!detail) return null;

    return {
      title: (modal.querySelector('[name="title"]') as HTMLInputElement).value.trim(),
      inspectionDate: (modal.querySelector('[name="inspectionDate"]') as HTMLInputElement).value,
      generalNotes: (modal.querySelector('[name="generalNotes"]') as HTMLTextAreaElement).value.trim() || undefined,
      sections: detail.sections.map((section) => ({
        id: section.id,
        items: section.items.map((item) => ({
          id: item.id,
          condition: (modal.querySelector(`[data-item-condition="${item.id}"]`) as HTMLSelectElement).value,
          comment: (modal.querySelector(`[data-item-comment="${item.id}"]`) as HTMLTextAreaElement).value.trim() || undefined,
        })),
      })),
    };
  };

  const uploadPhoto = async (itemId: string, file: File) => {
    if (!detail) return;

    busyItemId = itemId;
    formError = '';
    render();

    try {
      const upload = await placeDescriptionsApi.createPhotoUpload(detail.id, itemId, {
        fileName: file.name,
        contentType: file.type || 'image/jpeg',
        fileSizeBytes: file.size,
      });
      const uploadResponse = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'image/jpeg' },
        body: file,
      });
      if (!uploadResponse.ok) {
        await placeDescriptionsApi.deletePhoto(upload.photoId).catch(() => undefined);
        throw new Error(translate('document.uploadError'));
      }
      await load();
    } catch (err) {
      console.error('Failed to upload place description photo:', err);
      formError = err instanceof ApiError ? err.message : (err instanceof Error ? err.message : translate('common.errorGeneric'));
    } finally {
      busyItemId = '';
      render();
    }
  };

  const render = () => {
    if (loading) {
      modal.innerHTML = `<div class="modal-header"><h2>${translate('placeDescription.editorTitle')}</h2><button class="modal-close" data-action="close">${TENANT_ICONS.close}</button></div><div class="empty-state"><p>${translate('common.loading')}</p></div>`;
      modal.querySelector('[data-action="close"]')?.addEventListener('click', close);
      return;
    }

    if (!detail) {
      modal.innerHTML = `<div class="modal-header"><h2>${translate('placeDescription.editorTitle')}</h2><button class="modal-close" data-action="close">${TENANT_ICONS.close}</button></div><div class="empty-state"><p>${escapeHtml(error || translate('placeDescription.loadError'))}</p></div>`;
      modal.querySelector('[data-action="close"]')?.addEventListener('click', close);
      return;
    }

    modal.innerHTML = `
      <div class="modal-header">
        <div>
          <h2>${translate('placeDescription.editorTitle')}</h2>
          <p class="modal-subtitle">${escapeHtml(detail.tenantName)} · ${escapeHtml(detail.propertyAddress)}</p>
        </div>
        <button class="modal-close" data-action="close">${TENANT_ICONS.close}</button>
      </div>
      <form>
        <div class="form-error" ${formError ? '' : 'hidden'}>${escapeHtml(formError)}</div>
        <div class="place-description-meta-grid">
          <div class="form-group">
            <label>${translate('common.title')}</label>
            <input name="title" value="${escapeHtml(detail.title)}" required>
          </div>
          <div class="form-group">
            <label>${translate('common.type')}</label>
            <input value="${escapeHtml(translatePlaceDescriptionKind(detail.kind))}" disabled>
          </div>
          <div class="form-group">
            <label>${translate('common.date')}</label>
            <input name="inspectionDate" type="date" value="${detail.inspectionDate}" required>
          </div>
          <div class="form-group">
            <label>${translate('placeDescription.templateLabel')}</label>
            <input value="${escapeHtml(detail.templateName)}" disabled>
          </div>
        </div>
        <div class="form-group">
          <label>${translate('common.notes')}</label>
          <textarea name="generalNotes" rows="4" placeholder="${translate('placeDescription.notesPlaceholder')}">${escapeHtml(detail.generalNotes || '')}</textarea>
        </div>
        <div class="place-description-sections">
          ${detail.sections.map((section) => `
            <section class="place-description-section">
              <div class="place-description-section-header">
                <h3>${escapeHtml(section.name)}</h3>
              </div>
              <div class="place-description-items">
                ${section.items.map((item) => `
                  <div class="place-description-item">
                    <div class="place-description-item-header">
                      <strong>${escapeHtml(item.label)}</strong>
                      <div class="place-description-item-status">
                        <label for="place-description-condition-${item.id}">${translate('common.status')}</label>
                        <select id="place-description-condition-${item.id}" class="form-control" data-item-condition="${item.id}">
                          ${['Good', 'NeedsAttention', 'Damaged', 'NotApplicable'].map((condition) => `<option value="${condition}" ${item.condition === condition ? 'selected' : ''}>${escapeHtml(translatePlaceDescriptionCondition(condition))}</option>`).join('')}
                        </select>
                      </div>
                    </div>
                    <textarea class="form-control place-description-item-comment" rows="3" data-item-comment="${item.id}" placeholder="${translate('placeDescription.itemCommentPlaceholder')}">${escapeHtml(item.comment || '')}</textarea>
                    <div class="place-description-photo-toolbar">
                      <label class="btn btn-outline btn-sm place-description-upload-btn ${busyItemId === item.id ? 'is-busy' : ''}">
                        <input type="file" accept="image/*" data-item-upload="${item.id}" ${busyItemId === item.id ? 'disabled' : ''} hidden multiple>
                        ${busyItemId === item.id ? translate('common.uploading') : translate('placeDescription.addPhoto')}
                      </label>
                    </div>
                    ${item.photos.length === 0
                      ? `<p class="place-description-photo-empty">${translate('placeDescription.noPhotos')}</p>`
                      : `<div class="place-description-photo-grid">
                          ${item.photos.map((photo) => `
                            <div class="place-description-photo-card">
                              <div class="place-description-photo-name">${escapeHtml(photo.fileName)}</div>
                              <div class="place-description-photo-actions">
                                <button type="button" class="btn btn-outline btn-sm" data-action="view-photo" data-photo-id="${photo.id}">${translate('common.download')}</button>
                                <button type="button" class="btn btn-danger btn-sm" data-action="delete-photo" data-photo-id="${photo.id}">${translate('common.delete')}</button>
                              </div>
                            </div>
                          `).join('')}
                        </div>`}
                  </div>
                `).join('')}
              </div>
            </section>
          `).join('')}
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="close">${translate('common.cancel')}</button>
          <button type="button" class="btn btn-outline" data-action="export">${translate('common.export')}</button>
          <button type="submit" class="btn btn-primary" ${saving ? 'disabled' : ''}>${saving ? translate('common.saving') : translate('common.save')}</button>
        </div>
      </form>
    `;

    modal.querySelectorAll('[data-action="close"]').forEach((button) => button.addEventListener('click', close));
    modal.querySelector('[data-action="export"]')?.addEventListener('click', async () => {
      try {
        formError = '';
        const file = await placeDescriptionsApi.export(placeDescriptionId);
        downloadBlob(file.blob, file.fileName);
      } catch (err) {
        console.error('Failed to export place description:', err);
        formError = err instanceof Error ? err.message : translate('placeDescription.exportError');
        render();
      }
    });

    modal.querySelectorAll<HTMLInputElement>('[data-item-upload]').forEach((input) => input.addEventListener('change', async () => {
      if (!input.files?.length) return;
      for (const file of Array.from(input.files)) {
        await uploadPhoto(input.dataset.itemUpload!, file);
      }
      input.value = '';
    }));

    modal.querySelectorAll<HTMLElement>('[data-action="view-photo"]').forEach((button) => button.addEventListener('click', async () => {
      try {
        const photoId = button.dataset.photoId!;
        const photo = detail?.sections
          .flatMap((section) => section.items)
          .flatMap((item) => item.photos)
          .find((item) => item.id === photoId);
        await placeDescriptionsApi.downloadPhoto(photoId, photo?.fileName);
      } catch (err) {
        console.error('Failed to open photo:', err);
      }
    }));

    modal.querySelectorAll<HTMLElement>('[data-action="delete-photo"]').forEach((button) => button.addEventListener('click', async () => {
      if (!await confirmDialog(translate('placeDescription.confirmDeletePhoto'))) {
        return;
      }

      try {
        formError = '';
        await placeDescriptionsApi.deletePhoto(button.dataset.photoId!);
        await load();
      } catch (err) {
        console.error('Failed to delete photo:', err);
        formError = err instanceof Error ? err.message : translate('common.errorGeneric');
        render();
      }
    }));

    modal.querySelector('form')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      formError = '';
      saving = true;
      render();

      try {
        detail = await placeDescriptionsApi.update(placeDescriptionId, buildUpdatePayload());
      } catch (err) {
        console.error('Failed to save place description:', err);
        formError = err instanceof Error ? err.message : translate('common.errorGeneric');
      } finally {
        saving = false;
        render();
      }
    });
  };

  overlay.appendChild(modal);
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  document.body.appendChild(overlay);
  await load();
}
